import * as helios from "@hyperionbt/helios";
import cx from "clsx";
import { Artwork } from "kreate-common/modules/business-types";
import ButtonPrimary from "kreate-common/modules/kreate-ui/components/ButtonPrimary";
import ButtonTransparentV2 from "kreate-common/modules/kreate-ui/components/ButtonTransparentV2";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { CLIENT_ENV } from "kreate-env/client";
import { buildTx$BuyArtwork } from "kreate-protocol/offchain/tx/buyArtwork";
import { httpGet$GetAssetOnChainData } from "kreate-server/commands/GetAssetOnChainData/fetcher";
import { httpGet$GetProtocolParams } from "kreate-server/commands/GetProtocolParams/fetcher";
import { httpPost$UpdateMarketplaceListing } from "kreate-server/commands/UpdateMarketplaceListing/fetcher";
import React from "react";
import { MdEdit, MdFavorite } from "react-icons/md";
import useSWR from "swr";

import DashedHorizontalDivider from "../../../../components/DashedHorizontalDivider";
import InputNumEditions$Compact from "../../../../components/InputNumEditions$Compact";

import styles from "./index.module.scss";
import { getNumEditionsInWallet } from "./utils";

import { useBusyCount } from "@/modules/common-hooks/useBusyCount";
import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import { waitForUtxo } from "@/modules/common-utils/async";
import {
  formatLovelaceAmount$Compact,
  formatLovelaceAmount$Standard,
} from "@/modules/common-utils/format";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import { EM_DASH } from "@/modules/common-utils/unicode";
import { useField$Natural } from "@/modules/form-field/hooks/useField$Natural";
import InlineErrorViewer from "@/modules/kreate-components/components/InlineErrorViewer";
import { useModalTrigger$ModalConnectWallet } from "@/modules/kreate-modals/modals/ModalConnectWallet/hooks/useModalTrigger$ModalConnectWallet";
import { useModalTrigger$ModalProgress } from "@/modules/kreate-modals/modals/ModalProgress/hooks/useModalTrigger$ModalProgress";
import { useModalTrigger$ModalSuccess } from "@/modules/kreate-modals/modals/ModalSuccess/hooks";
import { useModalTrigger$ModalTransactionBreakdown } from "@/modules/kreate-modals/modals/ModalTransactionBreakdown/hooks";
import { useModalTrigger$ModalUpdateListingDetailsConfirmation } from "@/modules/kreate-modals/modals/ModalUpdateListingDetailsConfirmation/hooks";
import { useModalTrigger$ModalUpdateListingDetailsPrompt } from "@/modules/kreate-modals/modals/ModalUpdateListingDetailsPrompt/hooks";

// TODO: @kien-kreate: get these constants from protocol params
const KREATOR_MINT_FEE = 500_000;
const MIN_PROTOCOL_FEE = 2_000_000;

type Props = {
  className?: string;
  style?: React.CSSProperties;
  artwork: Artwork | null | undefined;
};

export default function MethodMarketplace({
  className,
  style,
  artwork,
}: Props) {
  const modalTriggers = {
    ModalConnectWallet: useModalTrigger$ModalConnectWallet(),
    ModalUpdateListingDetailsPrompt:
      useModalTrigger$ModalUpdateListingDetailsPrompt(),
    ModalUpdateListingDetailsConfirmation:
      useModalTrigger$ModalUpdateListingDetailsConfirmation(),
    ModalProgress: useModalTrigger$ModalProgress(),
    ModalSuccess: useModalTrigger$ModalSuccess(),
    ModalTransactionBreakdown: useModalTrigger$ModalTransactionBreakdown(),
  };

  const { busy, withDefaultErrorHandler } = useBusyCount();

  const loginStatus = useLoginStatus();

  const canUserMakeChanges =
    loginStatus?.loggedIn &&
    (loginStatus.isAgent || artwork?.createdBy === loginStatus.userId);

  const artwork_assetId = artwork?.assetId;
  const artwork_utxo = artwork?.utxo;

  const { data: assetOnChainData } = useSWR(
    artwork_assetId
      ? ["345ab563-6084-4561-91f1-a0b45bcf2552", artwork_assetId]
      : undefined,
    async () =>
      await httpGet$GetAssetOnChainData(
        `/api/v1/asset-onchain-data/${artwork_assetId}`
      )
  );

  const artworkDatum = artwork?.utxo?.datum;

  const listingPrice =
    artworkDatum?.type === "marketplace"
      ? artworkDatum.value.editionPrice
      : undefined;

  const numEditions =
    artworkDatum?.type === "marketplace"
      ? artwork?.utxo?.otherAssets[0]?.tokens[0]?.tokenValue || 0
      : undefined;

  const totalSupply = assetOnChainData?.quantity;

  const isSoldOut = !!artwork?.utxo && !numEditions;

  const fieldNumEditionsToBuy = useField$Natural({
    initialValue: 1,
    min: 1,
    max: numEditions ?? 1000000,
  });

  const handleUpdateListingDetails =
    loginStatus?.loggedIn &&
    loginStatus.userId === artwork?.createdBy &&
    listingPrice != null &&
    numEditions != null &&
    artwork_assetId
      ? withDefaultErrorHandler(async () => {
          const cip30Wallet =
            await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();
          const utxos = await cip30Wallet.utxos;

          // NOTE: Because `getNumEditionsInWallet` takes time, we use
          // `Promise.all` to improve UX instead of letting user wait
          const [promptFormData, numEditionsInWallet, baseAddress] =
            await Promise.all([
              modalTriggers.ModalUpdateListingDetailsPrompt.openModal({
                initialListingPrice: listingPrice,
                initialNumEditions: numEditions,
              }),
              getNumEditionsInWallet(cip30Wallet, artwork_assetId),
              new helios.WalletHelper(cip30Wallet).baseAddress,
            ]);

          const confirmationFormData =
            await modalTriggers.ModalUpdateListingDetailsConfirmation.openModal(
              {
                listingPrice: promptFormData.listingPrice,
                numEditionsOld: numEditions,
                numEditionsNew: promptFormData.numEditions,
                numEditionsInWallet,
              }
            );

          const numEditionsTakenFromWallet = (() => {
            switch (confirmationFormData.type) {
              case "skip-wallet-editions":
                return 0;
              case "transfer-from-wallet":
                return confirmationFormData.numEditions;
              case "transfer-to-wallet":
                return -confirmationFormData.numEditions;
            }
          })();

          await modalTriggers.ModalProgress.openModal$AsyncGenerator(
            async function* () {
              yield {
                text: "Building transaction...",
                ratio: 0.0,
                ratioUpTo: 0.1,
                shrinkRatio: 0.9375,
              };

              const res = await httpPost$UpdateMarketplaceListing(
                "/api/v1/tx/update-listing",
                {
                  artworkId: artwork.artworkId,
                  address: baseAddress.toBech32(),
                  utxosCBOR: utxos.map((utxo) =>
                    helios.bytesToHex(utxo.toCbor())
                  ),
                  noMintNew:
                    promptFormData.numEditions -
                    numEditions -
                    numEditionsTakenFromWallet,
                  noFromWallet: numEditionsTakenFromWallet,
                  price: promptFormData.listingPrice,
                }
              );

              const tx = helios.Tx.fromCbor(res.tx);

              const confirmed =
                await modalTriggers.ModalTransactionBreakdown.openModal(
                  [
                    {
                      label: "Platform Fee",
                      lovelaceAmount: res.protocolFee || undefined,
                      tooltip: res.protocolFee
                        ? `${formatLovelaceAmount$Compact(
                            KREATOR_MINT_FEE
                          )} × No. Editions Transferred To Wallet, minimum ${formatLovelaceAmount$Compact(
                            MIN_PROTOCOL_FEE
                          )}`
                        : undefined,
                    },
                    {
                      label: "Transaction Fee",
                      lovelaceAmount: res.txFee,
                      tooltip:
                        "The blockchain network fees to process this transaction",
                    },
                  ],
                  { artwork }
                );

              if (!confirmed) {
                throw null;
              }

              yield {
                text: "Waiting for signature...",
                ratio: 0.1,
                ratioUpTo: 0.2,
                shrinkRatio: 0.9375,
              };

              const signatures = await cip30Wallet.signTx(tx);
              tx.addSignatures(signatures);

              yield {
                text: "Waiting for submission...",
                ratio: 0.2,
                ratioUpTo: 0.3,
                shrinkRatio: 0.9375,
              };

              const txId = await cip30Wallet.submitTx(tx);

              yield {
                text: `Waiting for confirmation...`,
                ratio: 0.3,
                ratioUpTo: 1.0,
                shrinkRatio: 0.9375,
              };

              await waitForUtxo(cip30Wallet, (utxo) => utxo.txId.eq(txId));
              await shamelesslyRevalidateEverything();
            }
          );

          await modalTriggers.ModalSuccess.openModal({
            icon: <MdFavorite />,
            content: (
              <>
                <span>{"There are now "}</span>
                <strong>{promptFormData.numEditions}</strong>
                <span>{" Editions listed on Kreate ("}</span>
                <strong
                  title={formatLovelaceAmount$Standard(
                    promptFormData.listingPrice
                  )}
                >
                  {formatLovelaceAmount$Compact(promptFormData.listingPrice)}
                </strong>
                <strong>{"/Edition"}</strong>
                <span>{")."}</span>
              </>
            ),
            buttons: [{ content: "Close", onClick: "close" }],
          });
        })
      : undefined;

  const handleBuy =
    artwork_utxo && !isSoldOut
      ? withDefaultErrorHandler(async () => {
          const numEditionsToBuy =
            fieldNumEditionsToBuy.validateBeforeSubmitting();

          const cip30Wallet =
            await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();

          await modalTriggers.ModalProgress.openModal$AsyncGenerator(
            async function* () {
              yield {
                text: "Fetching protocol parameters...",
                ratio: 0.0,
                ratioUpTo: 0.1,
                shrinkRatio: 0.9375,
              };

              const protocolParamsUTxO = await httpGet$GetProtocolParams(
                "/api/v1/utxo/protocol-params"
              );

              yield {
                text: "Building transaction...",
                ratio: 0.1,
                ratioUpTo: 0.2,
                shrinkRatio: 0.9375,
              };

              const { tx, protocolFee, buyFee, txFee, isSeller } =
                await buildTx$BuyArtwork(
                  CLIENT_ENV.CARDANO_NETWORK,
                  cip30Wallet,
                  protocolParamsUTxO,
                  artwork_utxo,
                  numEditionsToBuy,
                  `『${artwork.title}』 was just purchased!`
                );

              const confirmed =
                await modalTriggers.ModalTransactionBreakdown.openModal(
                  [
                    {
                      label: "Artwork Price",
                      lovelaceAmount: isSeller ? null : buyFee,
                      tooltip: isSeller ? null : "Listing Price × No. Editions",
                    },
                    {
                      label: "Platform Fee",
                      lovelaceAmount: protocolFee,
                      tooltip: isSeller
                        ? `${formatLovelaceAmount$Compact(
                            KREATOR_MINT_FEE
                          )} × No. Editions, minimum ${formatLovelaceAmount$Compact(
                            MIN_PROTOCOL_FEE
                          )}`
                        : `2% of Artwork Price (minimum ${formatLovelaceAmount$Compact(
                            MIN_PROTOCOL_FEE
                          )})`,
                    },
                    {
                      label: "Transaction Fee",
                      lovelaceAmount: txFee,
                      tooltip:
                        "The blockchain network fees to process this transaction",
                    },
                  ],
                  { artwork }
                );
              if (!confirmed) {
                throw null;
              }

              yield {
                text: "Waiting for signature and submission...",
                ratio: 0.2,
                ratioUpTo: 0.3,
                shrinkRatio: 0.9375,
              };

              const signatures = await cip30Wallet.signTx(tx);
              tx.addSignatures(signatures);
              const txId = await cip30Wallet.submitTx(tx);

              yield {
                text: "Waiting for confirmation...",
                ratio: 0.3,
                ratioUpTo: 1.0,
                shrinkRatio: 0.9375,
              };

              await waitForUtxo(cip30Wallet, (utxo) => utxo.txId.eq(txId));
              await shamelesslyRevalidateEverything();
            }
          );

          await modalTriggers.ModalSuccess.openModal({
            icon: <MdFavorite />,
            content: (
              <>
                <span>{"You just purchased "}</span>
                <strong>{numEditionsToBuy}</strong>
                <span>{" Editions."}</span>
              </>
            ),
            buttons: [{ content: "Close", onClick: "close" }],
          });
        })
      : undefined;

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Col className={styles.content}>
        <Flex.Row
          className={styles.stats}
          padding="16px 32px"
          alignItems="center"
          gap="16px 32px"
          flex-wrap="wrap"
        >
          <dl>
            <dt>{"Price"}</dt>
            <dd>
              <span title={formatLovelaceAmount$Standard(listingPrice)}>
                {formatLovelaceAmount$Compact(listingPrice)}
              </span>
              {canUserMakeChanges ? (
                <ButtonTransparentV2
                  smart={false}
                  content={<MdEdit className={styles.iconEdit} size="20px" />}
                  onClick={handleUpdateListingDetails}
                  disabled={busy || !handleUpdateListingDetails}
                />
              ) : undefined}
            </dd>
          </dl>
          <dl>
            <dt>{"Available"}</dt>
            <dd>
              <span>{numEditions ?? EM_DASH}</span>
              {canUserMakeChanges ? (
                <ButtonTransparentV2
                  smart={false}
                  content={<MdEdit className={styles.iconEdit} size="20px" />}
                  onClick={handleUpdateListingDetails}
                  disabled={busy || !handleUpdateListingDetails}
                />
              ) : undefined}
            </dd>
          </dl>
          <dl>
            <dt>{"Total Supply"}</dt>
            <dd>{totalSupply ?? EM_DASH}</dd>
          </dl>
        </Flex.Row>
        <DashedHorizontalDivider />
        <Flex.Col padding="16px 32px" gap="8px">
          <Flex.Row gap="8px" flexWrap="wrap">
            <Flex.Cell flex="1 1 auto" minWidth="200px" maxWidth="200px">
              <InputNumEditions$Compact.UseField$Natural
                api={fieldNumEditionsToBuy}
                disabled={busy || isSoldOut}
              />
            </Flex.Cell>
            <Flex.Cell asChild flex="10000 10000 0" minWidth="0">
              <ButtonPrimary
                style={
                  isSoldOut
                    ? { backgroundColor: "rgba(0, 0, 0, 36%)" }
                    : undefined
                }
                size="lg"
                content={isSoldOut ? "Sold Out" : "Purchase Now"}
                disabled={!handleBuy}
                onClick={handleBuy}
              />
            </Flex.Cell>
          </Flex.Row>
          {fieldNumEditionsToBuy.error ? (
            <Flex.Cell>
              <InlineErrorViewer
                content={fieldNumEditionsToBuy.error.message}
              />
            </Flex.Cell>
          ) : undefined}
        </Flex.Col>
      </Flex.Col>
    </div>
  );
}
